'use client';

import { Box, Typography, TextField, Grid, MenuItem } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

interface JobDetailsStepProps {
  details: {
    title: string;
    description: string;
    budget?: number;
    timeline?: string;
  };
  onChange: (field: string, value: any) => void;
}

const timelineOptions = [
  { value: 'flexible', label: 'Flexible' },
  { value: '1-week', label: 'Within 1 week' },
  { value: '2-weeks', label: 'Within 2 weeks' },
  { value: '1-month', label: 'Within 1 month' },
  { value: '3-months', label: 'Within 3 months' },
  { value: 'custom', label: 'Custom date' },
];

export default function JobDetailsStep({ details, onChange }: JobDetailsStepProps) {
  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(field, event.target.value);
  };

  const handleBudgetChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Allow empty value or positive numbers
    if (value === '' || (!isNaN(Number(value)) && Number(value) >= 0)) {
      onChange('budget', value === '' ? undefined : Number(value));
    }
  };

  return (
    <div>
      <Typography variant="h5" gutterBottom>
        Job Details
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Provide details about your job to help makers understand your needs.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            label="Job Title"
            placeholder="e.g., 3D Printed Enclosure for Electronics Project"
            value={details.title}
            onChange={handleChange('title')}
            fullWidth
            required
            helperText="A clear title helps makers understand your project quickly"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Description"
            placeholder="Describe your project, including any specific requirements or preferences..."
            value={details.description}
            onChange={handleChange('description')}
            multiline
            rows={4}
            fullWidth
            required
            helperText="Include any important details that weren't covered in the previous steps"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="Budget (£)"
            placeholder="Optional"
            value={details.budget || ''}
            onChange={handleBudgetChange}
            fullWidth
            type="number"
            InputProps={{
              inputProps: { min: 0 },
            }}
            helperText="Leave blank if you're flexible on budget"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            select
            label="Timeline"
            value={details.timeline || 'flexible'}
            onChange={handleChange('timeline')}
            fullWidth
            helperText="When do you need this completed?"
          >
            {timelineOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>
    </div>
  );
}
